/**
 * Lead source tree helpers. The API returns the tenant's lead sources as a
 * flat list; these order it as a tree and answer hierarchy questions.
 * Mirrors backend LeadSourceBusinessLayer (the mock API client uses these to
 * compute paths and inherited UTM values the way the backend does).
 */

import { UTM_FIELDS, UtmValues } from '../api/types';

/** The fields the hierarchy helpers need. */
export interface LeadSourceTreeNode {
  id: string;
  parentId: string | null;
  name: string;
}

function childrenByParent<T extends LeadSourceTreeNode>(nodes: T[]): Map<string | null, T[]> {
  const ids = new Set(nodes.map((n) => n.id));
  const map = new Map<string | null, T[]>();
  for (const node of nodes) {
    // A node whose parent is missing from the list is treated as a root.
    const key = node.parentId && ids.has(node.parentId) ? node.parentId : null;
    const siblings = map.get(key) ?? [];
    siblings.push(node);
    map.set(key, siblings);
  }
  map.forEach((siblings) => siblings.sort((a, b) => a.name.localeCompare(b.name)));
  return map;
}

/**
 * Depth-first (pre-order) ordering: each node is followed by its descendants,
 * siblings sorted by name. Suitable for rendering an indented tree.
 */
export function treeOrder<T extends LeadSourceTreeNode>(nodes: T[]): T[] {
  const children = childrenByParent(nodes);
  const result: T[] = [];
  const seen = new Set<string>();
  const visit = (node: T) => {
    if (seen.has(node.id)) return;
    seen.add(node.id);
    result.push(node);
    (children.get(node.id) ?? []).forEach(visit);
  };
  (children.get(null) ?? []).forEach(visit);
  return result;
}

/** IDs of every descendant of `id` (excluding `id` itself). */
export function descendantIds(id: string, nodes: LeadSourceTreeNode[]): string[] {
  const children = childrenByParent(nodes);
  const result: string[] = [];
  const seen = new Set<string>([id]);
  const queue = [id];
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const child of children.get(current) ?? []) {
      if (!seen.has(child.id)) {
        seen.add(child.id);
        result.push(child.id);
        queue.push(child.id);
      }
    }
  }
  return result;
}

/** The node followed by its ancestors up to the root (cycle-safe). */
export function ancestorChain<T extends LeadSourceTreeNode>(id: string, nodes: T[]): T[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const chain: T[] = [];
  const visited = new Set<string>();
  let current = byId.get(id);
  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    chain.push(current);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return chain;
}

/** Names from the root down to the node. */
export function pathNames(id: string, nodes: LeadSourceTreeNode[]): string[] {
  return ancestorChain(id, nodes).map((n) => n.name).reverse();
}

/**
 * Per UTM field, the first non-null value walking from the node up through
 * its ancestors (the backend's getEffectiveUtm).
 */
export function effectiveUtm(id: string, nodes: (LeadSourceTreeNode & UtmValues)[]): UtmValues {
  const chain = ancestorChain(id, nodes);
  const result = {} as UtmValues;
  for (const field of UTM_FIELDS) {
    result[field] = chain.find((n) => n[field] !== null && n[field] !== undefined)?.[field] ?? null;
  }
  return result;
}

/** Display labels for the UTM fields. */
export const UTM_FIELD_LABELS: Record<(typeof UTM_FIELDS)[number], string> = {
  utmSource: 'utm_source',
  utmMedium: 'utm_medium',
  utmCampaign: 'utm_campaign',
  utmTerm: 'utm_term',
  utmContent: 'utm_content',
};
